'use client'

import { useState } from 'react'
import { UnifiedFeedLazy } from '@/feed'
import { useDebounce } from '@/lib/use-debounce'
import { useViewMode } from '@/feed'
import { usePersistedState } from '@/lib/use-persisted-state'
import { useWeeklyReflection } from '@/resurfacing'
import { WeeklyReflectionCard } from '@/resurfacing'
import { cn } from '@/lib/tailwind'
import type { FilterType } from '@/feed'
import type { PromptCategory } from './meta'

type JournalFilter = 'journals' | 'all'

const FILTER_OPTIONS: Array<{ value: JournalFilter; label: string }> = [
  { value: 'journals', label: 'Journal' },
  { value: 'all', label: 'All Entries' },
]

const DISPLAY_CATEGORIES: Array<{ value: PromptCategory; label: string }> = [
  { value: 'joy-gratitude', label: 'Gratitude' },
  { value: 'challenge-anxiety', label: 'Challenge' },
  { value: 'accomplishment', label: 'Accomplishment' },
  { value: 'people-connection', label: 'Connection' },
  { value: 'self-awareness', label: 'Self-awareness' },
  { value: 'hyperfocus-energy', label: 'Energy' },
  { value: 'emotional-reaction', label: 'Emotion' },
  { value: 'weekly-review', label: 'Weekly review' },
]

/** Maps the journal toggle value to the feed's FilterType */
function toFilterType(filter: JournalFilter): FilterType {
  return filter === 'journals' ? 'journals' : 'all'
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'bg-accent/15 text-accent border-accent/30'
          : 'text-muted-foreground/60 border-border/30 hover:text-muted-foreground bg-transparent',
      )}
      aria-pressed={active}
    >
      {label}
    </button>
  )
}

export function JournalView() {
  const [journalFilter, setJournalFilter] = usePersistedState<JournalFilter>(
    'journal-filter',
    'journals',
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] =
    useState<PromptCategory | null>(null)
  const { viewMode, setViewMode, availableModes } = useViewMode({
    storageKey: 'journal-view-mode',
    defaultMode: 'list',
    availableModes: ['list', 'timeline'],
  })

  const debouncedSearch = useDebounce(searchQuery, 300)
  const { reflectionEntry } = useWeeklyReflection()

  const showReflection = reflectionEntry && journalFilter === 'journals'

  // Reset category filter when switching away from journals mode
  const handleFilterChange = (filter: JournalFilter) => {
    setJournalFilter(filter)
    if (filter !== 'journals') {
      setSelectedCategory(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      {/* Header: segmented control + view switcher */}
      <div className="flex items-center justify-between gap-3 py-4">
        {/* Segmented control */}
        <div
          className="bg-muted/60 flex rounded-lg p-1"
          role="radiogroup"
          aria-label="Journal filter"
        >
          {FILTER_OPTIONS.map((option) => {
            const isActive = journalFilter === option.value
            return (
              <button
                key={option.value}
                role="radio"
                aria-checked={isActive}
                onClick={() => handleFilterChange(option.value)}
                className={cn(
                  'flex items-center rounded-md px-4 py-1.5 text-sm font-medium',
                  'transition-all duration-200 ease-out',
                  'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                  'active:scale-[0.98]',
                  isActive
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Category filter chips */}
      {journalFilter === 'journals' && (
        <div
          className="flex flex-wrap gap-1.5 pb-3"
          data-testid="category-filters"
        >
          <FilterChip
            label="All"
            active={selectedCategory === null}
            onClick={() => setSelectedCategory(null)}
          />
          {DISPLAY_CATEGORIES.map((cat) => (
            <FilterChip
              key={cat.value}
              label={cat.label}
              active={selectedCategory === cat.value}
              onClick={() => setSelectedCategory(cat.value)}
            />
          ))}
        </div>
      )}

      {/* Weekly reflection card (pinned at top) */}
      {showReflection && (
        <div className="mb-6">
          <WeeklyReflectionCard entry={reflectionEntry} />
        </div>
      )}

      {/* Feed */}
      <UnifiedFeedLazy
        searchQuery={debouncedSearch}
        rawSearchQuery={searchQuery}
        selectedTopics={selectedTopics}
        onSearchChange={setSearchQuery}
        onTopicsChange={setSelectedTopics}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        availableViewModes={availableModes}
        defaultFilter={toFilterType(journalFilter)}
        promptCategoryFilter={selectedCategory ?? undefined}
      />
    </div>
  )
}
